import ScaleLine from 'ol/control/ScaleLine';
import { map } from './Map.js';

function init() {
  if (!map) {
    requestAnimationFrame(init);
    return;
  }

  // أضف المقياس مرة واحدة فقط
  if (!map) {
    requestAnimationFrame(init);
    return;
  }

  // Remove existing ScaleLine controls if any (cleanup)
  // We use class checking because HMR might mess up instanceof checks
  map.getControls().getArray()
    .slice() // Create a copy to avoid modification issues during iteration
    .forEach(c => {
      if (c instanceof ScaleLine || (c.element && c.element.classList.contains('ol-scale-line'))) {
        map.removeControl(c);
      }
    });

  // Also manually remove any stray elements if they exist in DOM (double safety)
  document.querySelectorAll('.ol-scale-line').forEach(el => el.remove());

  const scaleElement = document.getElementById('scale-ratio');

  let isEditing = false;

  const updateScale = () => {
    if (!scaleElement || isEditing) return;

    const view = map.getView();
    if (!view) return;

    const resolution = view.getResolution();

    if (resolution) {
      // Standard OGC Pixel size 0.28 mm = 0.00028 m
      const OGC_PIXEL_SIZE_M = 0.00028;
      const center = view.getCenter();
      const projection = view.getProjection();
      const mpu = projection.getMetersPerUnit();

      let resolutionMeters = resolution * mpu;

      if (projection.getCode() === 'EPSG:3857' && center) {
        const R = 6378137;
        const y = center[1];
        const lat = 2 * Math.atan(Math.exp(y / R)) - Math.PI / 2;
        resolutionMeters = resolution * mpu * Math.cos(lat);
      }

      const scale = Math.round(resolutionMeters / OGC_PIXEL_SIZE_M);

      if (!isNaN(scale)) {
        scaleElement.innerHTML = `<i class="fas fa-pen" style="font-size: 0.7em; margin-right: 5px; opacity: 0.5;"></i> 1:${scale.toLocaleString('en-US')}`;
        scaleElement.style.cursor = 'pointer';
        scaleElement.title = 'انقر لتعديل مقياس الرسم';
      }
    }
  };

  // Convert scale to resolution and zoom
  const zoomToScale = (targetScale) => {
    if (isNaN(targetScale) || targetScale <= 0) return;

    const view = map.getView();
    if (!view) return;

    const OGC_PIXEL_SIZE_M = 0.00028;
    const projection = view.getProjection();
    const mpu = projection.getMetersPerUnit();

    let targetResolution = (targetScale * OGC_PIXEL_SIZE_M) / mpu;

    if (projection.getCode() === 'EPSG:3857') {
      const center = view.getCenter();
      if (center) {
        const R = 6378137;
        const y = center[1];
        const lat = 2 * Math.atan(Math.exp(y / R)) - Math.PI / 2;
        targetResolution = targetResolution / Math.cos(lat);
      }
    }

    if (targetResolution) {
      view.animate({
        resolution: targetResolution,
        duration: 500
      });
    }
  };

  // Click event to set custom scale (Inline Edit)
  scaleElement.addEventListener('click', () => {
    if (isEditing) return;
    isEditing = true;

    // Get current scale number
    const currentText = scaleElement.textContent || '';
    // Extract digits
    const currentScale = currentText.replace(/[^0-9]/g, '');
    // Usually the text is "1:2,500" -> "12500" but we want just "2500"
    // Since we formatted it as `1:${scale}`, the regex might catch the '1' prefix if we aren't careful.
    // Let's parse it strictly.
    const parts = currentText.split(':');
    let initialValue = '1000';
    if (parts.length > 1) {
      initialValue = parts[1].replace(/[^0-9]/g, '');
    }

    scaleElement.innerHTML = '';

    // Create input container for styling
    const container = document.createElement('div');
    container.style.display = 'inline-flex';
    container.style.alignItems = 'center';
    container.style.gap = '5px';

    const prefix = document.createElement('span');
    prefix.textContent = '1:';

    const input = document.createElement('input');
    input.type = 'number';
    input.value = initialValue;
    input.style.width = '80px';
    input.style.padding = '2px 5px';
    input.style.border = '1px solid var(--primary)';
    input.style.borderRadius = '4px';
    input.style.fontSize = 'inherit';
    input.style.fontFamily = 'inherit';
    input.style.color = 'var(--text-primary)';
    input.style.background = 'var(--bg-secondary)';
    input.style.outline = 'none';

    container.appendChild(prefix);
    container.appendChild(input);
    scaleElement.appendChild(container);

    input.focus();
    input.select();

    const commitChange = () => {
      const val = parseFloat(input.value);
      isEditing = false;
      zoomToScale(val);
      updateScale(); // restore text view
    };

    const cancelChange = () => {
      isEditing = false;
      updateScale(); // restore text view
    };

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        commitChange();
      } else if (e.key === 'Escape') {
        cancelChange();
      }
      e.stopPropagation(); // prevent map interactions
    });

    input.addEventListener('blur', () => {
      // slight delay to allow interactions
      setTimeout(commitChange, 100);
    });

    // prevent click from bubbling to map
    input.addEventListener('click', (e) => e.stopPropagation());
  });

  // Listen to view changes
  map.getView().on('change:resolution', updateScale);
  map.on('moveend', updateScale);

  // Initial call
  setTimeout(updateScale, 500); // Small delay to ensure view is ready
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
